import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import {
  codeToLanguageCodes,
  isSupportedLanguageObject,
  LanguageCodes,
  LanguageObject,
} from "@flat-ui";
import { useLocalize } from "@faro-lotv/foreign-observers";
import { BrowserUtils } from "@stellar/utils";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { updateUserSetting } from "@store/settings/settings-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { transmitSettingsToParent } from "@utils/account-settings-utils";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo } from "react";

type OriginOfLanguageChange = "user menu" | "account settings";

interface ReturnProps {
  /** All the supported languages */
  supportedLanguages: LanguageObject[];

  /** Callback to update the language */
  updateLanguage: (
    languageOrigin: OriginOfLanguageChange,
    languageCode: LanguageCodes
  ) => void;

  /** The current selected language */
  currentLanguage: LanguageObject;
}

/** A hook that handles all the utility functions for language selector */
export function useLanguage(): ReturnProps {
  const {
    availableLanguages,
    setLanguage,
    currentLanguage: localizeLanguage,
  } = useLocalize();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const currentUserSettings = useAppSelector(userSettingsSelector);
  const dispatch = useAppDispatch();

  const supportedLanguages = useMemo(() => {
    return availableLanguages.filter(isSupportedLanguageObject);
  }, [availableLanguages]);


  const updateLanguage = useCallback(
    async (
      languageOrigin: OriginOfLanguageChange,
      languageCode: LanguageCodes
    ): Promise<void> => {

      trackEvent({
        name: UserProfile.changeLanguage,
        props: {
          language: languageCode,
          origin: languageOrigin,
          isLoggedIn: !!loggedInUser,
        },
      });

      setLanguage(languageCode);
      
      // Transmit settings to parent window
      if (BrowserUtils.isInIframe()) {
          transmitSettingsToParent({
            settingIdentifier: "language",
            settingValue: languageCode,
          });
        }

      if (!loggedInUser) {
        // If the user is not logged in just update the language through LocalizeJS
        return;
      }
      // Logged-in user => update backend first, then LocalizeJS
      try {
        const response = await dispatch(updateUserSetting({
          identifier: "language",
          userId: loggedInUser.id,
          payload: {
            value: codeToLanguageCodes(languageCode),
            modifiedAt: currentUserSettings.language?.modifiedAt ?? "",
          },
        })).unwrap();

        // Update LocalizeJS after successful backend update
       if (response.setting.identifier === "language" && typeof response.setting.value === "string") {
          setLanguage(response.setting.value);
        }
        // Transmit settings to parent window
        if (BrowserUtils.isInIframe()) {
          transmitSettingsToParent({
            settingIdentifier: "language",
            settingValue: response.setting.value,
          });
        }
      } catch (error) {
        handleErrorWithToast({
          id: `updateLanguage-${Date.now().toString()}`,
          title: "Failed to update the user language setting.",
          error,
        });
      }
    },
    [trackEvent, loggedInUser, setLanguage, dispatch, currentUserSettings, handleErrorWithToast]
  );

  return {
    supportedLanguages,
    updateLanguage,
    currentLanguage: {
      code: codeToLanguageCodes(localizeLanguage.code),
      name: localizeLanguage.name,
    },
  };
}
